import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { gsap, ScrollTrigger } from "../../utils/gsapSetup";
import ProjectCards from "../../utils/ProjectCards";
import { fetchActiveCategories, fetchProjectsByCategory } from "../../services/firestore";
import { getCategorySlug, resolveLocalizedField } from "../../utils/i18nHelpers";
import websites from "../../data/websites";

// Ana sayfada sadece ilk 1 projeyi göster
const HOMEPAGE_LIMIT = 1;

const WebServices = () => {
    const { t, i18n } = useTranslation();
    const sectionRef = useRef(null);
    const [visible, setVisible] = useState([]);
    const [displaySites, setDisplaySites] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dile özgü açıklama çözücü (JSON formunda kaydedilmiş olabilir)
    const getDesc = (site) => {
        if (site.desc_tr) return site[`desc_${i18n.language}`] || site.desc_tr;
        return resolveLocalizedField(site.desc, site.title);
    };

    const getTitle = (site) => {
        if (site.name) return site.name;
        return resolveLocalizedField(site.title, site.title);
    };

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const categories = await fetchActiveCategories();
                const webServicesCat = categories.find(
                    (cat) => getCategorySlug(cat, "tr") === "web-hizmetleri"
                );

                let loadedProjects = [];
                if (webServicesCat) {
                    loadedProjects = await fetchProjectsByCategory(webServicesCat.id);
                }

                if (loadedProjects && loadedProjects.length > 0) {
                    setDisplaySites(loadedProjects.slice(0, HOMEPAGE_LIMIT));
                } else {
                    setDisplaySites(websites.slice(0, HOMEPAGE_LIMIT));
                }
            } catch (err) {
                console.error("Web Services verileri yüklenemedi:", err);
                setDisplaySites(websites.slice(0, HOMEPAGE_LIMIT));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [i18n.language]);

    useEffect(() => {
        setVisible(new Array(displaySites.length).fill(false));
    }, [displaySites]);

    useEffect(() => {
        if (!sectionRef.current || displaySites.length === 0) return;

        const triggers = [];
        const cards = sectionRef.current.querySelectorAll(".project-card");
        cards.forEach((card, idx) => {
            const anim = gsap.fromTo(
                card,
                { opacity: 0, y: 40 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: card,
                        start: "top 80%",
                        toggleActions: "play none none reverse",
                    },
                    onStart: () => {
                        setVisible((prev) =>
                            prev.map((v, i) => (i === idx ? true : v))
                        );
                    },
                }
            );
            if (anim.scrollTrigger) triggers.push(anim.scrollTrigger);
        });

        return () => {
            triggers.forEach((st) => st.kill());
        };
    }, [displaySites, visible.length]);

    return (
        <motion.section
            className="web-services-section projects bg-black" // added projects class for styling
            initial="offscreen"
            whileInView="onscreen"
            ref={sectionRef}
        >
            {displaySites.map((site, index) => {
                let cls = "left";
                if (index % 2 === 0) {
                    cls = "right";
                }

                return (
                    <div
                        key={site.id}
                        className={`project relative py-12 lg:py-24 xl:py-32 ${cls} project-card`}
                        data-index={index}
                    >
                        <motion.div
                            initial={{ opacity: 0, translateX: -30 }}
                            whileInView={{ opacity: 1, translateX: 0 }}
                            transition={{ duration: 0.6 }}
                            className={`contain flex lg:gap-36 gap-8 lg:gap-16 flex-col lg:flex-row relative -order-1 ${cls === "right" ? "text-black" : "text-white"}`}
                            style={{
                                flexDirection:
                                    cls === "right" ? "row-reverse" : "row",
                            }}
                        >
                            <div className="video-info lg:pr-16 px-4 lg:px-0 flex flex-col lg:w-[50%] justify-center">
                                <h4 className="text-xl lg:text-2xl mb-2 uppercase">
                                    {t("webServices.title")}
                                </h4>
                                <h2 className="text-3xl sm:text-5xl lg:text-[clamp(2.5rem,4.5vw,4.5rem)] font-bold uppercase mb-8 !leading-[1.15] lg:mb-16 break-words">
                                    {getTitle(site)}
                                </h2>
                                <p className="text-base font-light mb-8">
                                    {getDesc(site)}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 mx-auto lg:mx-0">
                                    <a
                                        href={site.video_url || site.videoURL || site.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`px-10 font-semibold max-w-fit py-4 border-[2px] ${cls === "right" ? "bg-white text-black border-white hover:bg-black hover:text-white lg:bg-black lg:text-white lg:border-black lg:hover:bg-white lg:hover:text-black" : "bg-white text-black hover:bg-black hover:text-white border-white"}`}
                                    >
                                        {t("webServices.visitSite")}
                                    </a>
                                    <Link
                                        to="/web-services"
                                        aria-label={`${t("actions.seeAll")}: ${t("webServices.title")}`}
                                        className={`px-10 font-semibold max-w-fit py-4 border-[2px] ${cls === "right" ? "bg-transparent text-white border-white lg:text-black lg:border-black hover:bg-white hover:text-black lg:hover:bg-black lg:hover:text-white" : "bg-transparent text-white border-white hover:bg-white hover:text-black"}`}
                                    >
                                        {t("actions.seeAll")}
                                    </Link>
                                </div>
                            </div>
                            <div className="video-project relative aspect-w-16 aspect-h-9 overflow-hidden w-full lg:w-[50%]">
                                <div className="absolute inset-0 w-full h-full">
                                    {/* Browser chrome bar */}
                                    <div className="absolute top-0 left-0 right-0 h-8 bg-gray-800 rounded-t-lg flex items-center px-3 gap-2 z-10">
                                        <div className="flex gap-1.5">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        </div>
                                        <div className="flex-1 min-w-0 mx-2">
                                            <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-300 font-mono truncate">
                                                {(site.video_url || site.videoURL || site.url || "").replace("https://", "").replace("http://", "")}
                                            </div>
                                        </div>
                                    </div>
                                    {/* iframe */}
                                    {visible[index] ? (
                                        <iframe
                                            src={site.video_url || site.videoURL || site.url}
                                            title={getTitle(site)}
                                            className="absolute left-0 right-0 bottom-0 w-full rounded-b-lg border-0 bg-white"
                                            style={{
                                                top: "32px",
                                                height: "calc(100% - 32px)",
                                                pointerEvents: "none",
                                            }}
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div
                                            className="absolute left-0 right-0 bottom-0 bg-gray-900 rounded-b-lg flex items-center justify-center"
                                            style={{
                                                top: "32px",
                                                height: "calc(100% - 32px)",
                                            }}
                                        >
                                            <div className="loader2"></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </div>
                );
            })}


        </motion.section>
    );
};

export default WebServices;
